import { useState, useEffect, useRef, useCallback } from 'react';
import { siteConfigAPI, adminSupportAPI, adminAPI } from '../../../services/api';
import { logoAPI } from '../../../services/logoAPI';
import { useSiteConfig } from '../../../contexts/SiteConfigContext';
import { useNavigate } from 'react-router-dom';

export interface LogoItem {
  id: number;
  name: string;
  logo_type: string;
  image: string;
  image_url: string;
  alt_text: string;
  is_active: boolean;
  width?: number;
  height?: number;
}

export function useAdminSEO() {
  const { refreshConfig } = useSiteConfig();
  const navigate = useNavigate();
  const [pageStatus, setPageStatus] = useState({ loading: true, saving: false });
  const { loading, saving } = pageStatus;
  const [feedback, setFeedback] = useState({ success: '', error: '' });
  const { success, error } = feedback;
  const [unreadTickets, setUnreadTickets] = useState(0);

  // Site Branding
  const [branding, setBranding] = useState({
    siteName: '', siteTagline: '', siteDescription: '',
  });
  const { siteName, siteTagline, siteDescription } = branding;

  // Company Info
  const [companyInfo, setCompanyInfo] = useState({
    companyName: '', companyPhone: '', companyEmail: '', companyWebsite: '',
    companyAddress: '', businessHours: '', welcomeMessage: '',
  });
  const { companyName, companyPhone, companyEmail, companyWebsite, companyAddress, businessHours, welcomeMessage } = companyInfo;

  // SEO
  const [seoFields, setSeoFields] = useState({
    metaTitle: '', metaDescription: '', metaKeywords: '',
    ogTitle: '', ogDescription: '', ogImageUrl: '',
    canonicalUrl: '', googleAnalyticsId: '', gtmContainerId: '', robotsTxt: '',
  });
  const { metaTitle, metaDescription, metaKeywords, ogTitle, ogDescription, ogImageUrl, canonicalUrl, googleAnalyticsId, gtmContainerId, robotsTxt } = seoFields;

  // Social Media
  const [socialLinks, setSocialLinks] = useState({
    facebookUrl: '', instagramUrl: '', twitterUrl: '', linkedinUrl: '',
  });
  const { facebookUrl, instagramUrl, twitterUrl, linkedinUrl } = socialLinks;

  // Footer
  const [footerFields, setFooterFields] = useState({
    footerText: '', footerCopyright: '', footerTagline: '',
  });
  const { footerText, footerCopyright, footerTagline } = footerFields;

  // Rate Limits
  interface RateLimit { max_attempts: number; window_seconds: number; }
  const [rateLimits, setRateLimits] = useState<Record<string, RateLimit>>({});
  const [rateLimitsSaving, setRateLimitsSaving] = useState(false);

  // Geo Restriction
  const [geoRestriction, setGeoRestriction] = useState('worldwide');

  // Maintenance Mode
  const [maintenance, setMaintenance] = useState({ enabled: false, message: '' });

  // Terms Acceptances
  interface TermsRecord { id: number; username: string; company_name: string; email: string; accepted_at: string; ip_address: string; terms_version: string; }
  const [termsState, setTermsState] = useState<{ records: TermsRecord[]; total: number; page: number; loading: boolean; search: string }>({
    records: [], total: 0, page: 1, loading: false, search: '',
  });

  // Logos & Favicon
  const [logoState, setLogoState] = useState<{
    logos: LogoItem[];
    logosLoading: boolean;
    deleteConfirm: number | null;
  }>({ logos: [], logosLoading: false, deleteConfirm: null });
  const { logos, logosLoading, deleteConfirm } = logoState;

  // Upload modal state
  const [uploadState, setUploadState] = useState<{
    showUploadModal: boolean; uploadType: string; uploadName: string;
    uploadAlt: string; uploadFile: File | null; uploadPreview: string; uploading: boolean;
  }>({
    showUploadModal: false, uploadType: 'main', uploadName: '',
    uploadAlt: '', uploadFile: null, uploadPreview: '', uploading: false,
  });
  const { showUploadModal, uploadType, uploadName, uploadAlt, uploadFile, uploadPreview, uploading } = uploadState;
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadConfig();
    loadLogos();
    loadRateLimits();
    // Fetch unread ticket count (non-blocking)
    adminSupportAPI.getUnreadCount().then(res => {
      setUnreadTickets(res.data.unread_count || 0);
    }).catch(() => {});
  }, []);

  const loadConfig = async () => {
    try {
      setPageStatus(prev => ({ ...prev, loading: true }));
      const response = await siteConfigAPI.getConfig();
      const config = response.data;

      setBranding({
        siteName: config.site_name || '',
        siteTagline: config.site_tagline || '',
        siteDescription: config.site_description || '',
      });
      setCompanyInfo({
        companyName: config.company_name || '',
        companyPhone: config.company_phone || '',
        companyEmail: config.company_email || '',
        companyWebsite: config.company_website || '',
        companyAddress: config.company_address || '',
        businessHours: config.business_hours || '',
        welcomeMessage: config.welcome_message || '',
      });
      setSeoFields({
        metaTitle: config.meta_title || '',
        metaDescription: config.meta_description || '',
        metaKeywords: config.meta_keywords || '',
        ogTitle: config.og_title || '',
        ogDescription: config.og_description || '',
        ogImageUrl: config.og_image_url || '',
        canonicalUrl: config.canonical_url || '',
        googleAnalyticsId: config.google_analytics_id || '',
        gtmContainerId: config.gtm_container_id || '',
        robotsTxt: config.robots_txt || '',
      });
      setSocialLinks({
        facebookUrl: config.facebook_url || '',
        instagramUrl: config.instagram_url || '',
        twitterUrl: config.twitter_url || '',
        linkedinUrl: config.linkedin_url || '',
      });
      setFooterFields({
        footerText: config.footer_text || '',
        footerCopyright: config.footer_copyright || '',
        footerTagline: config.footer_tagline || '',
      });
      setGeoRestriction(config.geo_restriction || 'worldwide');
      setMaintenance({
        enabled: config.maintenance_mode || false,
        message: config.maintenance_message || '',
      });
    } catch (err) {
      setFeedback(prev => ({ ...prev, error: 'Failed to load configuration' }));
      console.error(err);
    } finally {
      setPageStatus(prev => ({ ...prev, loading: false }));
    }
  };

  const loadLogos = async () => {
    try {
      setLogoState(prev => ({ ...prev, logosLoading: true }));
      const response = await logoAPI.getAllLogos();
      const data = response.data;
      setLogoState(prev => ({ ...prev, logos: Array.isArray(data) ? data : (data?.results || []) }));
    } catch (err) {
      console.error('Failed to load logos:', err);
    } finally {
      setLogoState(prev => ({ ...prev, logosLoading: false }));
    }
  };

  const loadRateLimits = async () => {
    try {
      const res = await adminAPI.getRateLimits();
      setRateLimits(res.data.rate_limits || {});
    } catch (err) {
      console.error('Failed to load rate limits:', err);
    }
  };

  const handleSaveRateLimits = async () => {
    setRateLimitsSaving(true);
    setFeedback({ success: '', error: '' });
    try {
      const res = await adminAPI.updateRateLimits({ rate_limits: rateLimits });
      setRateLimits(res.data.rate_limits);
      setFeedback(prev => ({ ...prev, success: 'Rate limits saved successfully!' }));
      setTimeout(() => setFeedback(prev => ({ ...prev, success: '' })), 3000);
    } catch (err) {
      setFeedback(prev => ({ ...prev, error: 'Failed to save rate limits.' }));
    } finally {
      setRateLimitsSaving(false);
    }
  };

  const loadTermsAcceptances = useCallback(async (page = 1, search = '') => {
    setTermsState(prev => ({ ...prev, loading: true }));
    try {
      const res = await adminAPI.getTermsAcceptances({ page, search: search || undefined });
      setTermsState(prev => ({ ...prev, records: res.data.results, total: res.data.total, page: res.data.page, loading: false }));
    } catch {
      setTermsState(prev => ({ ...prev, loading: false }));
    }
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadState(prev => ({ ...prev, uploadFile: file }));
      const reader = new FileReader();
      reader.onloadend = () => setUploadState(prev => ({ ...prev, uploadPreview: reader.result as string }));
      reader.readAsDataURL(file);
    }
  };

  const openUploadModal = (type: string) => {
    const labels: Record<string, string> = { main: 'Main Logo', header: 'Header Logo', login: 'Login Logo', favicon: 'Favicon' };
    setUploadState({
      showUploadModal: true,
      uploadType: type,
      uploadName: labels[type] || type,
      uploadAlt: '',
      uploadFile: null,
      uploadPreview: '',
      uploading: false,
    });
  };

  const handleUploadLogo = async () => {
    if (!uploadFile) return;
    setUploadState(prev => ({ ...prev, uploading: true }));
    try {
      const formData = new FormData();
      formData.append('image', uploadFile);
      formData.append('name', uploadName);
      formData.append('logo_type', uploadType);
      formData.append('alt_text', uploadAlt || uploadName);
      formData.append('is_active', 'true');
      await logoAPI.createLogo(formData);
      setUploadState(prev => ({ ...prev, showUploadModal: false }));
      await loadLogos();
      setFeedback(prev => ({ ...prev, success: 'Logo uploaded successfully!' }));
      setTimeout(() => setFeedback(prev => ({ ...prev, success: '' })), 3000);
    } catch (err: any) {
      setFeedback(prev => ({ ...prev, error: err.response?.data?.detail || 'Failed to upload logo.' }));
    } finally {
      setUploadState(prev => ({ ...prev, uploading: false }));
    }
  };

  const handleDeleteLogo = async (id: number) => {
    try {
      await logoAPI.deleteLogo(id);
      setLogoState(prev => ({ ...prev, deleteConfirm: null }));
      await loadLogos();
      setFeedback(prev => ({ ...prev, success: 'Logo deleted.' }));
      setTimeout(() => setFeedback(prev => ({ ...prev, success: '' })), 3000);
    } catch (err) {
      setFeedback(prev => ({ ...prev, error: 'Failed to delete logo.' }));
    }
  };

  const handleToggleActive = async (logo: LogoItem) => {
    try {
      const formData = new FormData();
      formData.append('is_active', (!logo.is_active).toString());
      await logoAPI.updateLogo(logo.id, formData);
      await loadLogos();
    } catch (err) {
      setFeedback(prev => ({ ...prev, error: 'Failed to update logo status.' }));
    }
  };

  const getLogosByType = (type: string) => logos.filter(l => l.logo_type === type);
  const getActiveLogo = (type: string) => logos.find(l => l.logo_type === type && l.is_active);

  const handleSave = async (section: string) => {
    setPageStatus(prev => ({ ...prev, saving: true }));
    setFeedback({ success: '', error: '' });

    let data: any = {};

    switch (section) {
      case 'branding':
        data = { site_name: branding.siteName, site_tagline: branding.siteTagline, site_description: branding.siteDescription };
        break;
      case 'company':
        data = {
          company_name: companyInfo.companyName, company_phone: companyInfo.companyPhone,
          company_email: companyInfo.companyEmail, company_website: companyInfo.companyWebsite,
          company_address: companyInfo.companyAddress, business_hours: companyInfo.businessHours,
          welcome_message: companyInfo.welcomeMessage
        };
        break;
      case 'seo':
        data = {
          meta_title: seoFields.metaTitle, meta_description: seoFields.metaDescription,
          meta_keywords: seoFields.metaKeywords, og_title: seoFields.ogTitle,
          og_description: seoFields.ogDescription, og_image_url: seoFields.ogImageUrl,
          canonical_url: seoFields.canonicalUrl, google_analytics_id: seoFields.googleAnalyticsId,
          gtm_container_id: seoFields.gtmContainerId,
          robots_txt: seoFields.robotsTxt
        };
        break;
      case 'social':
        data = {
          facebook_url: socialLinks.facebookUrl, instagram_url: socialLinks.instagramUrl,
          twitter_url: socialLinks.twitterUrl, linkedin_url: socialLinks.linkedinUrl
        };
        break;
      case 'footer':
        data = {
          footer_text: footerFields.footerText, footer_copyright: footerFields.footerCopyright,
          footer_tagline: footerFields.footerTagline
        };
        break;
      case 'geo':
        data = { geo_restriction: geoRestriction };
        break;
      case 'maintenance':
        data = { maintenance_mode: maintenance.enabled, maintenance_message: maintenance.message };
        break;
    }

    try {
      await siteConfigAPI.updateConfig(data);
      await refreshConfig();
      setFeedback(prev => ({ ...prev, success: `${section.charAt(0).toUpperCase() + section.slice(1)} settings saved successfully!` }));
      setTimeout(() => setFeedback(prev => ({ ...prev, success: '' })), 3000);
    } catch (err) {
      setFeedback(prev => ({ ...prev, error: 'Failed to save settings. Make sure you have admin permissions.' }));
      console.error(err);
    } finally {
      setPageStatus(prev => ({ ...prev, saving: false }));
    }
  };

  const navigateToSupport = () => navigate('/manage/support');

  return {
    // Page status
    loading, saving,
    // Feedback
    success, error, feedback, setFeedback,
    // Tickets
    unreadTickets, navigateToSupport, navigate,
    // Branding
    siteName, siteTagline, siteDescription, branding, setBranding,
    // Company Info
    companyName, companyPhone, companyEmail, companyWebsite, companyAddress, businessHours, welcomeMessage, companyInfo, setCompanyInfo,
    // SEO
    metaTitle, metaDescription, metaKeywords, ogTitle, ogDescription, ogImageUrl, canonicalUrl, googleAnalyticsId, gtmContainerId, robotsTxt, seoFields, setSeoFields,
    // Social
    facebookUrl, instagramUrl, twitterUrl, linkedinUrl, socialLinks, setSocialLinks,
    // Footer
    footerText, footerCopyright, footerTagline, footerFields, setFooterFields,
    // Logos
    logos, logosLoading, deleteConfirm, logoState, setLogoState,
    getLogosByType, getActiveLogo,
    // Upload
    showUploadModal, uploadType, uploadName, uploadAlt, uploadFile, uploadPreview, uploading, uploadState, setUploadState,
    fileInputRef,
    handleFileSelect, openUploadModal, handleUploadLogo, handleDeleteLogo, handleToggleActive,
    // Rate Limits
    rateLimits, setRateLimits, rateLimitsSaving, handleSaveRateLimits,
    // Geo Restriction
    geoRestriction, setGeoRestriction,
    // Maintenance Mode
    maintenance, setMaintenance,
    // Terms Acceptances
    termsState, loadTermsAcceptances, setTermsState,
    // Save
    handleSave,
  };
}
